import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList, Bell, MoreHorizontal, Search, MessageCircle } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import ChatDialog from "../components/ChatDialog";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const weekAgo = new Date(now.getTime() - 6 * 86400000);
  if (d > weekAgo) return d.toLocaleDateString([], { weekday: "long" });
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};

const previewOf = (c, t) => {
  const type = c.last_message_type || c.last_type;
  let body = c.last_message || c.last_text || "";
  if (type === "image" || /\[image\]/i.test(body)) body = "[Image]";
  else if (type === "video" || /\[video\]/i.test(body)) body = "[Video message]";
  return body;
};

export default function Messenger() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [convs, setConvs] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [showNotifs, setShowNotifs] = useState(false);
  const [chip, setChip] = useState("all");
  const [q, setQ] = useState("");
  const [chatConv, setChatConv] = useState(null);

  const load = () => api("/conversations", { auth: true }).then(setConvs).catch(() => setConvs([]));

  useEffect(() => {
    if (user === undefined) return;
    if (!user) { navigate("/login?next=/messages"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openNotifs = () => {
    setShowNotifs((v) => !v);
    if (notifs === null) api("/notifications", { auth: true }).then((r) => setNotifs(Array.isArray(r) ? r : r.items || [])).catch(() => setNotifs([]));
  };

  const markAllRead = async () => {
    try { await api("/notifications/read", { method: "POST", auth: true }); } catch {}
    setNotifs((n) => (n || []).map((x) => ({ ...x, read: true })));
  };

  const list = useMemo(() => {
    let l = convs || [];
    if (chip === "unread") l = l.filter((c) => (c.unread || 0) > 0);
    if (chip === "ordered") l = l.filter((c) => c.has_order || c.order_id || c.ordered);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      l = l.filter((c) =>
        [c.partner_name, c.partner_company, c.last_message].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return l;
  }, [convs, chip, q]);

  if (!user) return null;

  const unreadNotifs = (notifs || []).filter((n) => !n.read).length;

  return (
    <div data-testid="messenger-page" className="mx-auto max-w-3xl px-4 pb-16 pt-5 sm:px-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("messenger_t")}</h1>

      {/* Quick actions */}
      <div className="mt-4 grid grid-cols-3">
        <Link data-testid="msgr-quick-orders" to="/account/orders" className="flex flex-col items-center gap-1.5 rounded-2xl py-2 transition hover:bg-slate-50 active:scale-95">
          <ClipboardList className="h-6 w-6 text-slate-700" strokeWidth={1.7} />
          <span className="text-xs font-medium text-slate-700">{t("tab_orders")}</span>
        </Link>
        <button data-testid="msgr-quick-notifs" onClick={openNotifs} className="relative flex flex-col items-center gap-1.5 rounded-2xl py-2 transition hover:bg-slate-50 active:scale-95">
          <span className="relative">
            <Bell className="h-6 w-6 text-slate-700" strokeWidth={1.7} />
            {unreadNotifs > 0 && <span className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">{unreadNotifs}</span>}
          </span>
          <span className="text-xs font-medium text-slate-700">{t("notifs_t")}</span>
        </button>
        <button data-testid="msgr-quick-others" onClick={() => setChip(chip === "others" ? "all" : "others")} className="flex flex-col items-center gap-1.5 rounded-2xl py-2 transition hover:bg-slate-50 active:scale-95">
          <MoreHorizontal className="h-6 w-6 text-slate-700" strokeWidth={1.7} />
          <span className="text-xs font-medium text-slate-700">{t("others_t")}</span>
        </button>
      </div>

      {/* Notifications panel */}
      {showNotifs && (
        <div data-testid="notifs-panel" className="mt-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
          <div className="flex items-center justify-between">
            <p className="font-heading text-sm font-bold text-slate-900">{t("notifs_t")}</p>
            <button data-testid="notifs-read-all" onClick={markAllRead} className="text-xs font-semibold text-teal-700 hover:underline">{t("mark_read")}</button>
          </div>
          {notifs === null ? (
            <p className="py-4 text-center text-xs text-slate-400">...</p>
          ) : notifs.length === 0 ? (
            <p data-testid="notifs-empty" className="py-4 text-center text-xs text-slate-400">{t("no_notifs")}</p>
          ) : (
            <div className="mt-2 divide-y divide-slate-50">
              {notifs.slice(0, 10).map((n, i) => (
                <div key={n.id || i} data-testid={`notif-row-${i}`} className="py-2.5">
                  <p className={`text-sm ${n.read ? "text-slate-500" : "font-semibold text-slate-900"}`}>{n.title || n.message || n.text}</p>
                  {n.created_at && <p className="text-[10px] text-slate-400">{fmtTime(n.created_at)}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="mt-4 flex items-center gap-2 rounded-full bg-slate-100 px-4 py-3">
        <Search className="h-5 w-5 shrink-0 text-slate-400" />
        <input
          data-testid="msgr-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("msgr_search_ph")}
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />
      </div>

      {/* Filter chips */}
      <div className="mt-3 flex gap-2">
        {["all", "unread", "ordered"].map((k) => (
          <button
            key={k}
            data-testid={`msgr-chip-${k}`}
            onClick={() => setChip(k)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition active:scale-95 ${
              chip === k ? "bg-teal-700 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-teal-400"
            }`}
          >
            {t(`chip_${k}`)}
          </button>
        ))}
      </div>

      {/* Conversation list */}
      {convs === null ? (
        <p data-testid="msgr-loading" className="py-10 text-center text-sm text-slate-500">...</p>
      ) : list.length === 0 ? (
        <div data-testid="msgr-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 py-12 text-center">
          <MessageCircle className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 font-heading font-semibold text-slate-800">{t("no_convs")}</p>
        </div>
      ) : (
        <div className="mt-2 divide-y divide-slate-100">
          {(chip === "others" ? list.filter((c) => !(c.unread > 0)) : list).map((c) => {
            const preview = previewOf(c, t);
            const thumb = c.product_image || c.last_product_image;
            const avatar = c.partner_avatar || c.partner_logo;
            return (
              <button
                key={c.id}
                data-testid={`msgr-conv-${c.id}`}
                onClick={() => setChatConv(c)}
                className="flex w-full items-center gap-3 py-3.5 text-left transition active:scale-[0.99]"
              >
                {avatar ? (
                  <img src={mediaUrl(avatar)} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-700/10 font-heading text-base font-extrabold text-teal-700">
                    {(c.partner_name || "S").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className={`truncate text-[15px] ${c.unread > 0 ? "font-extrabold text-slate-900" : "font-bold text-slate-800"}`}>{c.partner_name}</span>
                    <span className="shrink-0 text-[11px] text-slate-400">{fmtTime(c.updated_at)}</span>
                  </span>
                  {c.partner_company && <span className="block truncate text-xs text-slate-400">{c.partner_company}</span>}
                  <span className="mt-0.5 block truncate text-sm text-slate-500">
                    {c.unread > 0 && <span className="font-bold text-teal-600">[{t("unread_tag")}] </span>}
                    {preview}
                  </span>
                </span>
                {thumb && (
                  <img src={mediaUrl(thumb)} alt="" className="h-11 w-11 shrink-0 rounded-lg border border-slate-100 object-cover" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {chatConv && <ChatDialog open={!!chatConv} onOpenChange={(v) => !v && setChatConv(null)} conversation={chatConv} />}
    </div>
  );
}
